import IsoAlpha from 'iso-3166-1-alpha-2';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import React, { useMemo, useState, Fragment, useEffect } from 'react';

import antConfig from '@/ant-config';
import AppLoader from '@/react_app/common/AppLoader';
import Avatar from '@/react_app/common/Avatar';
import type {
	TableProps,
	//  RadioChangeEvent
} from '@/react_app/components/Atoms';
import {
	AppInput,
	AppButton,
	AppTypography,
	AppSelect,
	//  AppRadio, AppSpace,
	AppTable,
} from '@/react_app/components/Atoms';
import {
	OrganizationSettingsProps,
	UserDisplay,
	// BillingEmailsConfig,
	OrganizationSettings,
} from '@/react_app/components/BillingUsage/Settings/settings-types';
interface DataType {
	key: string;
	user: UserDisplay;
}
interface Field {
	placeholder: string;
	key: keyof OrganizationSettings;
}

const columns: TableProps<DataType>['columns'] = [
	{
		title: 'User',
		key: 'avatar',
		dataIndex: 'user',
		render: (user) => (
			<div className="flex items-center">
				<Avatar size={32} user={user} />
				<span className="text-sm text-colorText ml-2">{user.name || user.email}</span>
			</div>
		),
	},
	{
		key: 'role',
		title: 'Role',
		dataIndex: 'user',
		render: (user) => <div className="text-sm text-colorTextSecondary">{user.role}</div>,
	},
];

const SettingsUsageBillingOrganizationSettings = ({
	users,
	organizationSettings,
	// billingEmailsConfig,
	// onAddNewBillingContact,
	emailsReceivingReceipts,
	getOrganizationSettings,
	// onSaveBillingEmailsConfig,
	onSaveOrganizationSettings,
	// isLoadingBillingEmailsConfig,
	isLoadingOrganizationSettings,
}: OrganizationSettingsProps) => {
	// const [billingEmailsValue, setBillingEmailsValue] = useState(billingEmailsConfig);
	const [localOrganizationSettings, setLocalOrganizationSettings] = useState(organizationSettings);
	const [isFetchingOrganizationSettings, setIsFetchingOrganizationSettings] = useState(false);

	const tableData = useMemo<DataType[]>(() => users.map((user) => ({ key: `${user.id}`, user })), [users]);

	const selectOptions = useMemo(
		() =>
			IsoAlpha.getCodes().map((code: string) => ({
				value: code,
				label: IsoAlpha.getCountry(code),
			})),
		[IsoAlpha]
	);

	const onSelectChange = (code: string) => {
		setLocalOrganizationSettings((previousLocalCompanySettings) => ({ ...previousLocalCompanySettings, countryOrRegion: code }));
	};

	// const onChange = (e: RadioChangeEvent) => {
	// 	setBillingEmailsValue(e.target.value);
	// };

	const onInputChange = (key: keyof OrganizationSettings, e: React.ChangeEvent<HTMLInputElement>) => {
		setLocalOrganizationSettings((previousLocalCompanySettings) => ({ ...previousLocalCompanySettings, [key]: e.target.value }));
	};
	const formFields: Field[] = useMemo(
		() => [
			{ key: 'organizationName', placeholder: 'Organization name' },
			{ key: 'addressLine1', placeholder: 'Address line 1' },
			{ key: 'addressLine2', placeholder: 'Address line 2' },
			{ key: 'city', placeholder: 'City' },
			{ key: 'postalCode', placeholder: 'Postal code' },
		],
		[organizationSettings]
	);
	const isSaveOrganizationButtonDisabled = useMemo(
		() => isEqual(localOrganizationSettings, organizationSettings),
		[localOrganizationSettings, organizationSettings]
	);
	// const isSaveEmailsConfigButtonDisabled = useMemo(
	// 	() => isEqual(billingEmailsValue, billingEmailsConfig),
	// 	[billingEmailsValue, billingEmailsConfig]
	// );
	useEffect(() => {
		if (isEmpty(organizationSettings)) {
			setIsFetchingOrganizationSettings(true);
			getOrganizationSettings().then(() => {
				setIsFetchingOrganizationSettings(false);
			});
		}
	}, [getOrganizationSettings, organizationSettings]);
	useEffect(() => {
		setLocalOrganizationSettings(organizationSettings);
	}, [organizationSettings]);
	return (
		<div className="max-w-2xl">
			<AppTypography.Title className="mt-10" level={5}>
				Company name and Address
			</AppTypography.Title>
			<AppTypography.Paragraph className="text-colorTextSecondary text-[13px]">
				This information will be included on all billing invoices on your account.
			</AppTypography.Paragraph>
			{isFetchingOrganizationSettings ? (
				<AppLoader />
			) : (
				<div className="mb-10 space-y-4">
					{formFields.map((field) => (
						<label key={field.key} className="block">
							<span className="block text-[13px] leading-[22px] mb-2">{field.placeholder}</span>
							<AppInput
								placeholder={field.placeholder}
								value={localOrganizationSettings[field.key]}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange(field.key, e)}
							/>
						</label>
					))}
					<label className="block">
						<span className="block text-[13px] leading-[22px] mb-2">{'Country or region'}</span>
						<AppSelect
							showSearch
							className="w-full"
							options={selectOptions}
							onSelect={onSelectChange}
							placeholder={'Country or region'}
							value={localOrganizationSettings['countryOrRegion']}
							filterOption={(input, option) => (option?.label ?? '')?.toLowerCase()?.includes(input?.toLowerCase())}
						/>
					</label>

					<div>
						<AppButton
							type="primary"
							loading={isLoadingOrganizationSettings}
							disabled={isSaveOrganizationButtonDisabled}
							onClick={() => onSaveOrganizationSettings(localOrganizationSettings)}
							style={{ color: isSaveOrganizationButtonDisabled ? 'grey' : 'white' }}
						>
							Save Settings
						</AppButton>
					</div>
				</div>
			)}
			<AppTypography.Title className="mt-10" level={5}>
				Billing emails
			</AppTypography.Title>
			{/* <AppTypography.Paragraph className="text-colorText text-[13px]">When new users join your workspace:</AppTypography.Paragraph>
			<AppRadio.Group onChange={onChange} value={billingEmailsValue}>
				<AppSpace direction="vertical">
					<AppRadio value={BillingEmailsConfig.Invoices}>Send billing notifications for invoices only</AppRadio>
					<AppRadio value={BillingEmailsConfig.Notifications}>Send billing notifications as changes occur (at most once a day)</AppRadio>
				</AppSpace>
			</AppRadio.Group>

			<AppTypography.Paragraph className="text-colorTextSecondary text-[13px] mt-4">
				Regardless of your choice above, you’ll receive usage alerts at 75%, 90% and 100% of your quota.
			</AppTypography.Paragraph>
			<AppButton
				type="primary"
				loading={isLoadingBillingEmailsConfig}
				onClick={() => onSaveBillingEmailsConfig(billingEmailsValue)}
				disabled={isSaveEmailsConfigButtonDisabled || isLoadingBillingEmailsConfig}
			>
				Save Settings
			</AppButton> */}
			<AppTypography.Title className="mt-10" level={5}>
				Billing contacts
			</AppTypography.Title>
			<AppTypography.Paragraph className="text-colorText text-[13px]">
				Billing emails are sent to{' '}
				{emailsReceivingReceipts.map((email, index) => (
					<Fragment key={email}>
						<strong style={{ color: antConfig.token?.colorText }}>{email}</strong>
						{index === emailsReceivingReceipts.length - 2 ? ' and ' : index !== emailsReceivingReceipts.length - 1 ? ', ' : ''}
					</Fragment>
				))}
			</AppTypography.Paragraph>

			{/* <AppTypography.Paragraph className="text-colorTextSecondary text-[13px]">
				<strong className="text-colorText">You may add additional billing contacts to receive copies of all billing-related emails.</strong>{' '}
				Billing contacts may be able to see certain workspace details, including the name and email of the Primary Owner and the person who
				added them as a billing contact.
			</AppTypography.Paragraph> */}
			<AppTypography.Paragraph className="text-colorTextSecondary text-[13px]">
				<strong className="text-colorText">Note: Only certain Ply users can make billing and payment changes.</strong> Define billing
				managers in user roles in settings {'>'} team management.
			</AppTypography.Paragraph>
			{/* <AppButton onClick={onAddNewBillingContact} type="primary">
				+ Add a new billing contact
			</AppButton> */}
			<AppTable className="mt-10" columns={columns} dataSource={tableData} pagination={false} />
		</div>
	);
};

export default SettingsUsageBillingOrganizationSettings;
